import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ITabletDevice,
  ITabletPairLookup,
} from '../../Interfaces/ITabletDevice';
import { customFetcher } from '../useFetch';

const TABLETS_KEY = ['tabletdevice'];

export const useGetTabletDevices = () => {
  const {
    data: tabletDevices,
    isLoading: isLoadingTabletDevices,
    refetch: refetchTabletDevices,
  } = useQuery<ITabletDevice[]>({
    queryKey: TABLETS_KEY,
    queryFn: () => customFetcher({ endpoint: '/tabletdevice' }),
  });
  return { tabletDevices, isLoadingTabletDevices, refetchTabletDevices };
};

export const useTabletPairLookup = () => {
  const {
    mutateAsync: lookupTabletPair,
    isPending: isPendingLookupTabletPair,
  } = useMutation({
    mutationFn: (userCode: string) =>
      customFetcher({
        endpoint: `/tablet/pair/lookup/${encodeURIComponent(userCode.trim())}`,
      }) as Promise<ITabletPairLookup>,
  });
  return { lookupTabletPair, isPendingLookupTabletPair };
};

export const useTabletPairApprove = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: approveTabletPair,
    isPending: isPendingApproveTabletPair,
  } = useMutation({
    mutationFn: ({
      userCode,
      Name,
      Description,
    }: {
      userCode: string;
      Name: string;
      Description?: string;
    }) =>
      customFetcher({
        endpoint: `/tablet/pair/approve/${encodeURIComponent(userCode.trim())}`,
        method: 'POST',
        body: { Name, Description },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TABLETS_KEY });
    },
  });
  return { approveTabletPair, isPendingApproveTabletPair };
};

export const useTabletPairDeny = () => {
  const { mutateAsync: denyTabletPair, isPending: isPendingDenyTabletPair } =
    useMutation({
      mutationFn: (userCode: string) =>
        customFetcher({
          endpoint: `/tablet/pair/deny/${encodeURIComponent(userCode.trim())}`,
          method: 'POST',
        }),
    });
  return { denyTabletPair, isPendingDenyTabletPair };
};

export const useRenameTabletDevice = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: renameTabletDevice,
    isPending: isPendingRenameTabletDevice,
  } = useMutation({
    mutationFn: ({
      idTabletDevice,
      Name,
      Description,
    }: {
      idTabletDevice: number;
      Name?: string;
      Description?: string;
    }) =>
      customFetcher({
        endpoint: `/tabletdevice/${idTabletDevice}`,
        method: 'PUT',
        body: { Name, Description },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TABLETS_KEY });
    },
  });
  return { renameTabletDevice, isPendingRenameTabletDevice };
};

export interface ITabletPairingStart {
  idTabletPairingCode: number;
  token: string;
  /** Kurzer Einwahlcode als manueller Fallback (z.B. "K7M4-9XQR"). */
  shortCode: string;
  name: string;
  description?: string | null;
  serverUrl: string;
  expiresIn: number;
  expiresAt: string;
  qrPayload: string;
}

export interface ITabletPairingStatus {
  status: 'pending' | 'consumed' | 'expired';
  idTabletPairingCode: number;
  idTabletDevice?: number | null;
  Name?: string;
}

export const useStartQrPairing = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: startQrPairing, isPending: isPendingStartQrPairing } =
    useMutation({
      mutationFn: (body: { Name: string; Description?: string }) =>
        customFetcher({
          endpoint: '/tabletdevice/pair/qr',
          method: 'POST',
          body,
        }) as Promise<ITabletPairingStart>,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: TABLETS_KEY });
      },
    });
  return { startQrPairing, isPendingStartQrPairing };
};

export const useGetQrPairingStatus = (
  idTabletPairingCode: number | null,
  enabled: boolean
) => {
  const { data: pairingStatus, refetch: refetchPairingStatus } =
    useQuery<ITabletPairingStatus>({
      queryKey: ['tabletpairingcode', idTabletPairingCode],
      queryFn: () =>
        customFetcher({
          endpoint: `/tabletdevice/pair/qr/${idTabletPairingCode}`,
        }),
      enabled: enabled && idTabletPairingCode != null,
      refetchInterval: enabled ? 2000 : false,
    });
  return { pairingStatus, refetchPairingStatus };
};

export const useCancelQrPairing = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: cancelQrPairing, isPending: isPendingCancelQrPairing } =
    useMutation({
      mutationFn: (idTabletPairingCode: number) =>
        customFetcher({
          endpoint: `/tabletdevice/pair/qr/${idTabletPairingCode}`,
          method: 'DELETE',
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: TABLETS_KEY });
      },
    });
  return { cancelQrPairing, isPendingCancelQrPairing };
};

export const useUnpairTabletDevice = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: unpairTabletDevice,
    isPending: isPendingUnpairTabletDevice,
  } = useMutation({
    mutationFn: (idTabletDevice: number) =>
      customFetcher({
        endpoint: `/tabletdevice/${idTabletDevice}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TABLETS_KEY });
    },
  });
  return { unpairTabletDevice, isPendingUnpairTabletDevice };
};
